/**
 * Pure listing helpers — business rules and display formatting.
 *
 * All six functions are 1:1 moves out of AdminListingsPage.js (L1
 * extraction). No rule was reinterpreted: the co-living locking semantics,
 * the "Ganze Unit" label and the three German availability captions behave
 * exactly as before, which is what the L0 characterization net pins.
 */

export function otherListingsForUnit(listings, unitId, editingListingId) {
  if (!unitId) return [];
  return listings.filter((l) => l.unit_id === unitId && l.id !== editingListingId);
}

export function isUnitLocked(unit, listings, editingListingId) {
  if (!unit) return false;
  const others = otherListingsForUnit(listings, unit.id, editingListingId);
  if (others.length === 0) return false;
  if (!unit.is_coliving) return true;
  if (others.some((l) => !l.room_id)) return true;
  const assignedRoomIds = new Set(others.map((l) => l.room_id).filter(Boolean));
  const total = Math.max(0, Number(unit.rooms) || 0);
  return total > 0 && assignedRoomIds.size >= total;
}

export function allowedListingTypesForUnit(unit, listings, editingListingId) {
  if (!unit) return { apartmentAllowed: true, roomAllowed: true };
  const others = otherListingsForUnit(listings, unit.id, editingListingId);
  const hasApartment = others.some((l) => !l.room_id);
  const hasRoom = others.some((l) => !!l.room_id);
  return {
    apartmentAllowed: !hasApartment && !hasRoom,
    roomAllowed: !hasApartment,
  };
}

export function describeListingObject(listing, units) {
  const unit = units.find((u) => u.id === listing.unit_id);
  const shortId = unit?.short_unit_id || "—";
  const typeLabel = unit?.is_coliving
    ? "Co-Living"
    : (unit?.type || "Apartment");
  const roomLabel = listing.room_id
    ? `Room ${listing.room_name || listing.room_id.slice(0, 8)}`
    : "Ganze Unit";
  return { shortId, roomLabel, typeLabel };
}

export function availabilityLabel(s) {
  const v = (s || "available").toLowerCase();
  if (v === "occupied") return "Belegt";
  if (v === "unavailable") return "Nicht verfügbar";
  return "Verfügbar";
}

export function availabilityBadgeStyle(s) {
  const v = (s || "available").toLowerCase();
  if (v === "occupied") {
    return {
      bg: "rgba(251, 146, 60, 0.1)",
      color: "#fb923c",
      border: "rgba(251, 146, 60, 0.2)",
    };
  }
  if (v === "unavailable") {
    return {
      bg: "rgba(255, 255, 255, 0.05)",
      color: "#6b7a9a",
      border: "rgba(255, 255, 255, 0.08)",
    };
  }
  return {
    bg: "rgba(34, 197, 94, 0.1)",
    color: "#4ade80",
    border: "rgba(34, 197, 94, 0.2)",
  };
}

/**
 * The submit-time guards of the listing form, as a pure function.
 *
 * Returns the German error message the form should show, or null when the
 * submission may proceed. The checks and their order are a 1:1 move out of
 * handleSubmitCreate (L1 extraction) — the room requirement first, then the
 * three co-living collision rules, each short-circuiting exactly as before.
 */
export function validateListingSubmission({
  form,
  listingType,
  units,
  listings,
  editingListingId,
}) {
  if (listingType === "room" && !form.room_id.trim()) {
    return "Bei Listing-Typ «Single Room» bitte einen Room wählen oder room_id eingeben.";
  }

  const selectedUnit = units.find((u) => u.id === form.unit_id);
  if (selectedUnit && selectedUnit.is_coliving) {
    const { apartmentAllowed, roomAllowed } = allowedListingTypesForUnit(
      selectedUnit,
      listings,
      editingListingId
    );
    if (listingType === "apartment" && !apartmentAllowed) {
      return "Für diese Unit existieren bereits Zimmer-Listings. Kein Apartment-Listing möglich.";
    }
    if (listingType === "room" && !roomAllowed) {
      return "Für diese Unit existiert bereits ein Apartment-Listing. Keine Zimmer-Listings möglich.";
    }
    if (listingType === "room" && form.room_id.trim()) {
      const roomTaken = listings.some(
        (l) =>
          l.unit_id === form.unit_id &&
          l.room_id === form.room_id.trim() &&
          l.id !== editingListingId
      );
      if (roomTaken) {
        return "Dieses Zimmer ist bereits gelistet.";
      }
    }
  }

  return null;
}
